<script lang="ts">
	/**
	 * Invisible, zero-markup keyboard listener.
	 *
	 * Rendered as a component rather than a `<svelte:window on:keydown>` so that
	 * shortcut handling lives and dies with the block that owns it — put it inside
	 * an `{#if}` and the shortcut only exists while that UI is on screen. That is
	 * what makes it safe for modals and popovers, where a global handler would keep
	 * firing after the dialog is gone.
	 */

	import { createEventDispatcher, onMount } from 'svelte';

	const dispatch = createEventDispatcher();

	/**
	 * The key codes to listen for.
	 *
	 * These are physical `KeyboardEvent.code` values (`"Enter"`, `"Escape"`,
	 * `"KeyC"`), NOT `event.key` — so they are layout independent.
	 *
	 * [MDN Reference](https://developer.mozilla.org/docs/Web/API/KeyboardEvent/code)
	 */
	export let keyCodes: string[] = [];

	/** Fire on key release. Turn off for actions that should not repeat on hold. */
	export let keyUp: boolean = true;
	/** Fire on key press. Turn off when only the release edge matters. */
	export let keyDown: boolean = true;

	/**
	 * Listen during the capture phase and swallow the event.
	 *
	 * Use for modal-scoped shortcuts (Escape, Enter) that must win over anything
	 * else already listening — without it, an outer dialog would also react.
	 */
	export let exclusive: boolean = false;

	let keyCodeSet = new Set<string>();

	const fnMakeEventDetail = (event: KeyboardEvent, isDown: boolean) => {
		return {
			event,
			isDown,
			code: event.code,
			key: event.key
		};
	};

	const fnOnKeyDown = (event: KeyboardEvent) => {
		if (!keyDown) return;
		if (!keyCodeSet.has(event.code)) return;

		dispatch('key', fnMakeEventDetail(event, true));

		if (exclusive) {
			event.preventDefault();
			event.stopImmediatePropagation();
			event.stopPropagation();
		}
	};

	const fnOnKeyUp = (event: KeyboardEvent) => {
		if (!keyUp) return;
		if (!keyCodeSet.has(event.code)) return;

		dispatch('key', fnMakeEventDetail(event, false));

		if (exclusive) {
			event.preventDefault();
			event.stopImmediatePropagation();
			event.stopPropagation();
		}
	};

	// Rebuilt whenever `keyCodes` changes so callers can swap shortcuts at runtime.
	$: {
		keyCodeSet = new Set<string>(keyCodes);
	}

	onMount(() => {
		if (exclusive) {
			window.addEventListener('keydown', fnOnKeyDown, true);
			window.addEventListener('keyup', fnOnKeyUp, true);
			return () => {
				window.removeEventListener('keydown', fnOnKeyDown, true);
				window.removeEventListener('keyup', fnOnKeyUp, true);
			};
		} else {
			window.addEventListener('keydown', fnOnKeyDown);
			window.addEventListener('keyup', fnOnKeyUp);
			return () => {
				window.removeEventListener('keydown', fnOnKeyDown);
				window.removeEventListener('keyup', fnOnKeyUp);
			};
		}
	});
</script>
